// @ts-nocheck
import { useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { forEach, isObject } from 'lodash';
import { mapDetailsToScreeningEntities } from '../components/ReferralIsOm/entityUtils';
import { CoreUtils } from '../utils/Utils';
import { postBulkVerdict } from '../utils/BulkVerdict';
import { API_ENDPOINTS } from '../const/ApiEndpoints';
import { selectAuth } from '../state/slices/appSlice';
import useInitiate from './useInitiate';
import useRiskRating from './useRiskRating';
import useCommonReducer from './useCommonReducer';
import useTransactionScreening from './useTransactionScreening';
import useInitialScreening from './useInitialScreening';
import consts, {
  Dummy_PEP_DATA,
  Dummy_SANCTION_DATA,
  Dummy_AMS_DATA,
  DEFAULT_COMMENT,
} from '../const/Const';

/**
 * @function useOngoingScreening
 * @description a custom hook for Ongoing Screening page
 * @returns {object} object
 */
export default function useOngoingScreening() {
  const { uploadAttachedFiles, uploadFileOns3 } = useInitialScreening();
  const {
    getRiskParameters,
    industry,
    product,
    occupation,
    crrMandatoryParameterList,
    riskParameters,
    countryList,
    fetchCountryList,
  } = useInitiate();
  const { getTransactionParameters, channel, productType, transactionType } =
    useTransactionScreening();
  const { mergeResults, mergeAllDetails, sortByParty } = CoreUtils;
  const {
    fetchRiskList,
    resetRiskData,
    calculateRiskRating,
    riskList,
    riskValue,
    riskResult,
    setRiskList,
    setRiskValue,
    systemRisk,
    setSystemRisk,
    riskResponse,
  } = useRiskRating();
  const {
    dispatch,
    setInitialPageIndexOngoingMonitoring,
    setInitialPageSizeOngoingMonitoring,
  } = useCommonReducer();
  const { enqueueSnackbar } = useSnackbar();
  const [userList, setUserList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sanctionsList, setSanctionsList] = useState([]);
  const [pepList, setPepList] = useState([]);
  const [amsList, setAmsList] = useState([]);
  const [legalList, setLegalList] = useState([]);
  const [corporateList, setCorporateList] = useState([]);
  const [legalDocument, setLegalDocument] = useState([]);
  const [CustomerHistoryList, setCustomerHistoryList] = useState([]);
  const [fetchUsersPageCount, setFetchUsersPageCount] = useState(0);
  const [hasData, setHasData] = useState(true);
  const [totalUsers, setTotalUsers] = useState(0);
  const [ongoingScreeningTotalCount, setOngoingScreeningTotalCount] =
    useState(0);
  const [totalCustomer, setTotalCustomer] = useState();
  const [disabled, setdisabled] = useState(true);
  const [cardDetails, setCardDetails] = useState({});
  const [analystsList, setAnalystsList] = useState([]);
  const [referralList, setReferralList] = useState([]);
  const [riskDetails, setRiskDetails] = useState([]);
  const [templateList, setTemplateList] = useState([]);
  const [template, setTemplate] = useState([]);
  const fetchIdRef = useRef(0);
  const { user } = useSelector(selectAuth);

  const showLoaderEvent = new Event('showLoader', { bubbles: true });
  const hideLoaderEvent = new Event('hideLoader', { bubbles: true });

  /**
   * @function resetAllList
   * @description A function to reset all lists
   * @returns {undefined}
   * @example resetAllList()
   */
  const resetAllList = () => {
    setSanctionsList([]);
    setPepList([]);
    setAmsList([]);
    setLegalList([]);
    setCorporateList([]);
  };
  /**
   * @function fetchUserList
   * @description A function to fetch User List
   * @returns {Promise} returns an axios promise
   */
  const fetchUserList = () => {
    setIsLoading(true);
    axios
      .get(API_ENDPOINTS.ONGOING_SCREENING_ALL_ANALYST)
      .then((response) => {
        setAnalystsList(response.data);
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
      });
  };
  /**
   * @function fetchReferralsList
   * @description A function to fetch Referrals List
   * @returns {Promise} returns an axios promise
   */
  const fetchReferralsList = (queryParams) => {
    setIsLoading(true);
    axios
      .get(API_ENDPOINTS.ONGOING_SCREENING_REFERRALS, {
        params: queryParams,
      })
      .then((response) => {
        setReferralList(response.data);
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
      });
  };

  /**
   * @function fetchUsers
   * @description A function to fetch Users data
   * @returns {Promise} returns an axios promise
   */
  const fetchUsers = useCallback(
    async ({
      pageSize,
      pageIndex,
      query,
      orderBy,
      status,
      days,
      checkerLevel,
      formData,
      alertList,
      alert,
      bookmark,
      team_type,
      channel,
      product,
      transaction_type,
      bookmarked,
      assignedStatus,
    }) => {
      // Give this fetch an ID
      fetchIdRef.current += 1;
      const fetchId = fetchIdRef.current;

      // Your server could send back total page count.
      setIsLoading(true);

      try {
        const response = await axios.get(
          API_ENDPOINTS.ONGOING_SCREENING_ALERT_LIST,
          {
            params: {
              page: pageIndex + 1,
              screen: 'ongoing',
              ...(pageSize ? { pagesize: pageSize } : {}),
              query: query?.trim(),
              orderBy,
              checkerLevel,
              status,
              days,
              ...formData,
              alert: alert || '',
              alert_list: alertList ? alertList.join().toLowerCase() : '',
              ...(!bookmarked && {
                bookmark: bookmark !== '' ? bookmark : false,
              }),
              team_type,
              channel,
              product,
              transaction_type,
              is_assigned: assignedStatus,
            },
            loader: !query?.length,
          }
        );

        setIsLoading(false);

        if (fetchId === fetchIdRef.current) {
          setUserList(response.data);
          setTotalUsers(response.headers['x-total-count']);
          setFetchUsersPageCount(
            Math.ceil(response.headers['x-total-count'] / pageSize)
          );

          // Check if the last page is empty, and move to the previous page if needed
          if (response?.data?.length === 0 && pageIndex > 0) {
            const newPageIndex = Math.max(pageIndex - 1, 0);
            dispatch(setInitialPageSizeOngoingMonitoring(pageSize));
            dispatch(setInitialPageIndexOngoingMonitoring(newPageIndex));
            return fetchUsers({
              pageSize,
              pageIndex: newPageIndex,
              query,
              orderBy,
              status,
              days,
              checkerLevel,
              formData,
              alertList,
              alert,
              team_type,
            });
          }

          if (!Number(response.headers['x-total-count'])) {
            setHasData(false);
          }
        }

        return response;
      } catch (error) {
        setIsLoading(false);
        throw error;
      }
    },
    []
  );

  function buildModifiedFilters(filters) {
    const result = {};
    if (!isObject(filters)) return result;
    forEach(filters, (value, key) => {
      if (key === 'targetDateRange' && isObject(value)) {
        if (value?.start)
          result.targetStartDate = moment(value.start).format(
            consts.GLOBAL_DATE_FORMAT
          );
        if (value?.end)
          result.targetEndDate = moment(value.end).format(
            consts.GLOBAL_DATE_FORMAT
          );
        return;
      }
      if (key === 'initiatedDateRange' && isObject(value)) {
        if (value?.start)
          result.initiatedStartDate = moment(value.start).format(
            consts.GLOBAL_DATE_FORMAT
          );
        if (value?.end)
          result.initiatedEndDate = moment(value.end).format(
            consts.GLOBAL_DATE_FORMAT
          );
        return;
      }
      if (key === 'alerted') {
        result[key] =
          Array.isArray(value) && value.length === 1
            ? value.join(',')
            : Array.isArray(value)
            ? ''
            : value;
      } else {
        result[key] = Array.isArray(value) ? value.join(',') : value;
      }
    });
    return result;
  }
  const getOngoingScreeningData = ({ page = 0, pageSize = 5, filters }) => {
    const modifiedFilters = buildModifiedFilters(filters);

    const params = {
      page: page + 1 || '',
      pagesize: pageSize || '',
      alert: modifiedFilters.alerted || '',
      screen: 'ongoing',
      search_type: modifiedFilters.searchType || '',
      initiate_start_date: modifiedFilters.initiatedStartDate || '',
      initiate_end_date: modifiedFilters.initiatedEndDate || '',
      risk_rating: modifiedFilters.riskType || '',
      target_start_date: modifiedFilters.targetStartDate || '',
      target_end_date: modifiedFilters.targetEndDate || '',
      team_id: modifiedFilters.team || '',
      query: modifiedFilters.search || '',
      selectedUsers: modifiedFilters.analyst || '',
      alert_list: (modifiedFilters.alertListType || '').toLowerCase(),
      onboarding_type: modifiedFilters.onboarding_type || '',
      party: modifiedFilters.party || '',
      transaction_product_type: modifiedFilters.transaction_product_type || '',
      channel: modifiedFilters.channel || '',
      bookmark: modifiedFilters.bookmark || '',
      industry: modifiedFilters.industry || '',
      transaction_type: modifiedFilters.transaction_type || '',
      occupation: modifiedFilters.occupation || '',
      product: modifiedFilters.product || '',
      country: modifiedFilters.country || '',
      field1: modifiedFilters.field1 || '',
      ...(modifiedFilters.team_type
        ? { team_type: modifiedFilters.team_type }
        : {}),
      is_assigned: modifiedFilters?.assignedStatus || '',
      team: modifiedFilters?.horizontalTeam || '',
      search_source: modifiedFilters?.search_source || '',
      manager_id: modifiedFilters.manager || '',
      search_format: modifiedFilters?.search_format || '',
      match_filter: modifiedFilters?.match_filter || '',
    };
    return axios
      .get(API_ENDPOINTS.ONGOING_SCREENING_ALERT_LIST, {
        params,
        loader: false,
      })
      .then((res) => {
        setOngoingScreeningTotalCount(res.headers?.['x-total-count']);
        return {
          data: res.data,
          totalRowCount: res.headers?.['x-total-count'],
        };
      });
  };

  const getOngoingTransactionUsers = ({ page = 0, pageSize = 5, filters }) => {
    const modifiedFilters = buildModifiedFilters(filters);

    const params = {
      page: page + 1 || '',
      pagesize: pageSize || '',
      alert: modifiedFilters.alerted || '',
      screen: 'ongoing',
      search_type: modifiedFilters.searchType || '',
      initiate_start_date: modifiedFilters.initiatedStartDate || '',
      initiate_end_date: modifiedFilters.initiatedEndDate || '',
      risk_rating: modifiedFilters.riskType || '',
      target_start_date: modifiedFilters.targetStartDate || '',
      target_end_date: modifiedFilters.targetEndDate || '',
      team_id: modifiedFilters.team || '',
      query: modifiedFilters.search || '',
      selectedUsers: modifiedFilters.analyst || '',
      alert_list: (modifiedFilters.alertListType || '').toLowerCase(),
      onboarding_type: modifiedFilters.onboarding_type || '',
      party: modifiedFilters.party || '',
      transaction_product_type: modifiedFilters.transaction_product_type || '',
      channel: modifiedFilters.channel || '',
      bookmark: modifiedFilters.bookmark || '',
      industry: modifiedFilters.industry || '',
      transaction_type: modifiedFilters.transaction_type || '',
      occupation: modifiedFilters.occupation || '',
      product: modifiedFilters.product || '',
      country: modifiedFilters.country || '',
      field1: modifiedFilters.field1 || '',
      ...(modifiedFilters.team_type
        ? { team_type: modifiedFilters.team_type }
        : {}),
      is_assigned: modifiedFilters?.assignedStatus || '',
      team: modifiedFilters?.horizontalTeam || '',
      search_source: modifiedFilters?.search_source || '',
      manager_id: modifiedFilters.manager || '',
      search_format: modifiedFilters?.search_format || '',
      match_filter: modifiedFilters?.match_filter || '',
    };
    return axios
      .get(API_ENDPOINTS.ONGOING_SCREENING_TRANSACTION_ALERT_LIST, {
        params,
        loader: false,
      })
      .then((res) => {
        setOngoingScreeningTotalCount(res.headers?.['x-total-count']);
        return {
          data: res.data,
          totalRowCount: res.headers?.['x-total-count'],
        };
      });
  };
  /**
   * @function fetchTransactionUsers
   * @description A function to fetch Users data
   * @returns {Promise} returns an axios promise
   */
  const fetchTransactionUsers = useCallback(
    async ({
      pageSize,
      pageIndex,
      query,
      orderBy,
      status,
      days,
      checkerLevel,
      formData,
      alertList,
      alert,
      bookmark,
      team_type,
      channel,
      product,
      transaction_type,
      bookmarked,
    }) => {
      // Give this fetch an ID
      fetchIdRef.current += 1;
      const fetchId = fetchIdRef.current;

      // Your server could send back total page count.
      setIsLoading(true);

      try {
        const response = await axios.get(
          API_ENDPOINTS.ONGOING_SCREENING_TRANSACTION_ALERT_LIST,
          {
            params: {
              page: pageIndex + 1,
              screen: 'ongoing',
              ...(pageSize ? { pagesize: pageSize } : {}),
              query: query?.trim(),
              orderBy,
              checkerLevel,
              status,
              days,
              ...formData,
              alert: alert || '',
              alert_list: alertList ? alertList.join().toLowerCase() : '',
              ...(!bookmarked && {
                bookmark: bookmark !== '' ? bookmark : false,
              }),
              team_type,
              channel,
              product,
              transaction_type,
            },
            loader: !query?.length,
          }
        );

        setIsLoading(false);

        if (fetchId === fetchIdRef.current) {
          setUserList(response.data);
          setTotalUsers(response.headers['x-total-count']);
          setFetchUsersPageCount(
            Math.ceil(response.headers['x-total-count'] / pageSize)
          );

          // Check if the last page is empty, and move to the previous page if needed
          if (response?.data?.length === 0 && pageIndex > 0) {
            const newPageIndex = Math.max(pageIndex - 1, 0);
            dispatch(setInitialPageSizeOngoingMonitoring(pageSize));
            dispatch(setInitialPageIndexOngoingMonitoring(newPageIndex));
            return fetchTransactionUsers({
              pageSize,
              pageIndex: newPageIndex,
              query,
              orderBy,
              status,
              days,
              checkerLevel,
              formData,
              alertList,
              alert,
              team_type,
            });
          }

          if (!Number(response.headers['x-total-count'])) {
            setHasData(false);
          }
        }

        return response;
      } catch (error) {
        setIsLoading(false);
        throw error;
      }
    },
    []
  );

  /**
   * @function handleReview
   * @description A function to fetch review details
   * @returns {Promise} returns an axios promise
   */
  const handleReview = (queryParams) => {
    setIsLoading(true);
    resetAllList();

    const payload = {
      comment: queryParams?.comment,
      attachment: queryParams?.file?.name,
      results: queryParams?.results,
    };

    return axios
      .post(
        `${API_ENDPOINTS.ONGOING_REVIEW_DETAILS}${queryParams?.data?.search_id}/`,
        payload
      )
      .then(
        (response) => {
          setIsLoading(false);
          if (response?.data) {
            enqueueSnackbar(response?.data?.message, {
              variant: 'success',
            });
            setTimeout(() => {
              window.location.reload();
            }, 1000);
          }
        },
        (response) => {
          setIsLoading(false);
          enqueueSnackbar(response?.response?.data?.message, {
            variant: 'error',
          });
        }
      );
  };

  /**
   * @function fetchCardData
   * @description a function to fetch card data
   * @param {object} queryParams
   * @returns {Promise} returns an axios promise
   */
  const fetchCardData = (queryParams) => {
    setIsLoading(true);
    resetAllList();
    resetRiskData();
    return axios
      .get(
        `${API_ENDPOINTS.ONGOING_SCREENING_CARD_DETAILS}${queryParams.search_id}/`
      )
      .then(
        (response) => {
          setIsLoading(false);
          if (response?.data) {
            setCardDetails(response?.data);
            if (response.data?.failed_type) {
              const message = CoreUtils.getFailedTypeMessage(
                response.data.failed_type
              );
              if (message) {
                enqueueSnackbar(message, {
                  variant: 'warning',
                });
              }
            }
          }
        },
        () => {
          setIsLoading(false);
        }
      );
  };

  /**
   * @function fetchTransactionCardData
   * @description a function to fetch card data
   * @param {object} queryParams
   * @returns {Promise} returns an axios promise
   */
  const fetchTransactionCardData = (queryParams) => {
    setIsLoading(true);
    resetAllList();
    resetRiskData();

    const url = `${API_ENDPOINTS.ONGOING_SCREENING_TRANSACTION_CARD_DETAILS}${queryParams.transaction_id}/`;

    return Promise.all([
      axios.get(url),
      axios.get(url, { params: { need_entities_in_list: true } }),
    ]).then(
      ([baseResponse, entitiesResponse]) => {
        setIsLoading(false);

        const baseData = baseResponse?.data || {};
        const entitiesData = entitiesResponse?.data || {};

        const mergedData = {
          ...baseData,
          ...entitiesData,
        };

        const screeningEntities = mapDetailsToScreeningEntities(mergedData);

        setCardDetails({
          ...mergedData,
          screening_entities: screeningEntities,
        });

        if (mergedData?.failed_type) {
          const message = CoreUtils.getFailedTypeMessage(
            mergedData.failed_type
          );
          if (message) {
            enqueueSnackbar(message, {
              variant: 'warning',
            });
          }
        }
      },
      () => {
        setIsLoading(false);
      }
    );
  };

  /**
   * @function getRiskRating
   * @description a function to get risk rating fields data
   * @param {object} queryParams
   * @returns {Promise} returns an axios promise
   */
  const getRiskRating = (queryParams) => {
    setIsLoading(true);
    return axios
      .get(API_ENDPOINTS.UPDATE_RISK_RATING_FIELDS, {
        params: {
          review_id: queryParams,
        },
      })
      .then(
        (response) => {
          setIsLoading(false);
          if (response?.data) {
            setRiskDetails(response?.data);
          }
        },
        () => {
          setIsLoading(false);
        }
      );
  };
  /**
   * @function updateRiskRating
   * @description a function to update risk rating fields data
   * @param {object} data - data object
   * @returns {Promise} returns an axios promise
   */
  const updateRiskRating = (data) => {
    setIsLoading(true);
    const payload = {
      previous_search_id: data.search_id,
      ...data,
    };
    return axios.put(API_ENDPOINTS.UPDATE_RISK_RATING_FIELDS, payload).then(
      () => {
        setIsLoading(false);
        enqueueSnackbar('Search has been successfully reinitiated.', {
          variant: 'success',
        });
      },
      () => {
        setIsLoading(false);
      }
    );
  };

  /**
   * @function updateUser
   * @description a function to update user's data
   * @param {object} data
   * @returns {Promise} returns an axios promise
   */
  const updateUser = (data) => {
    setIsLoading(true);
    axios
      .patch(`${API_ENDPOINTS.ONGOING_SCREENING_CARD_DETAILS}${data.id}/`, data)
      .then(
        (response) => {
          setIsLoading(false);
          if (response.status === 200) {
            setdisabled(!disabled);
            enqueueSnackbar(response.data.Msg, {
              variant: 'success',
            });
          }
        },
        () => {
          setIsLoading(false);
        }
      );
  };
  /**
   * @function fetchSanctionsList
   * @description a function to fetch sanctions list
   * @param {object} queryParams
   * @returns {Promise} returns an axios promise
   */
  // All changes in this file respective to generateAI are Demo changes - sanctions, pep, ams
  const fetchSanctionsList = (queryParams) => {
    const isNameScreen = queryParams?.card_type === 'name_screen';
    const url = isNameScreen
      ? `${API_ENDPOINTS.ONGOING_SCREENING_CARD_DETAILS}${queryParams.id}/sanction`
      : `${API_ENDPOINTS.ONGOING_SCREENING_TRANSACTION_ALL_ALERT_LIST}${queryParams.id}/sanction`;
    const generateAI = queryParams?.generateAI;
    setIsLoading(true);
    axios
      .get(url)
      .then((response) => {
        const data = response?.data;
        if (isNameScreen) {
          const processedSanctionResult = data?.sanction_result?.map(
            (item) => ({
              ...item,
              resultType: 'sanctions',
            })
          );
          const processedSanctionCountryResult =
            data?.sanction_country_result?.map((item) => ({
              ...item,
              resultType: 'sanctionsCountry',
            }));
          const processedMnrlResult = data?.mnrl_result?.map((item) => ({
            ...item,
            resultType: 'sanctionsMnrl',
          }));

          const mergedResults = [
            ...processedSanctionResult,
            ...processedSanctionCountryResult,
            ...processedMnrlResult,
          ];

          if (generateAI) {
            const sortedResultWithAI = mergedResults.map((item, index) => ({
              ...item,
              comments:
                Dummy_SANCTION_DATA[queryParams.username]?.comments[index] ||
                DEFAULT_COMMENT,
              status:
                Dummy_SANCTION_DATA[queryParams.username]?.status[index] ||
                'notSelected',
            }));
            setSanctionsList(sortedResultWithAI);
          } else {
            setSanctionsList(mergedResults);
          }
        } else {
          const mergedSanctionResult = mergeResults(
            data,
            'sanction_result',
            'sanctions'
          );
          const mergedSanctionCountryResult = mergeResults(
            data,
            'sanction_country_result',
            'sanctionsCountry'
          );
          const mergedMnrlResult = mergeResults(
            data,
            'mnrl_result',
            'sanctionsMnrl'
          );
          const mergedResults = [
            ...mergedSanctionResult,
            ...mergedMnrlResult,
            ...mergedSanctionCountryResult,
          ];
          const sortedResult = sortByParty(mergedResults);
          if (generateAI) {
            const sortedResultWithAI = sortedResult.map((item, index) => ({
              ...item,
              comments:
                Dummy_SANCTION_DATA[queryParams.username]?.comments[index] ||
                DEFAULT_COMMENT,
              status:
                Dummy_SANCTION_DATA[queryParams.username]?.status[index] ||
                'notSelected',
            }));
            setSanctionsList(sortedResultWithAI);
          } else {
            setSanctionsList(sortedResult);
          }
        }
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
      });
  };
  /**
   * @function fetchPepList
   * @description a function to fetch PEP list
   * @param {object} queryParams
   * @returns {Promise} returns an axios promise
   */
  const fetchPepList = (queryParams) => {
    const isNameScreen = queryParams?.card_type === 'name_screen';
    const url = isNameScreen
      ? `${API_ENDPOINTS.ONGOING_SCREENING_CARD_DETAILS}${queryParams.id}/pep`
      : `${API_ENDPOINTS.ONGOING_SCREENING_TRANSACTION_ALL_ALERT_LIST}${queryParams.id}/pep`;
    const generateAI = queryParams?.generateAI;
    setIsLoading(true);
    axios
      .get(url)
      .then((response) => {
        const { data } = response;
        if (isNameScreen) {
          if (generateAI) {
            const sortedResultWithAI = data.map((item, index) => ({
              ...item,
              comments:
                Dummy_PEP_DATA[queryParams.username]?.comments[index] ||
                DEFAULT_COMMENT,
              status:
                Dummy_PEP_DATA[queryParams.username]?.status[index] ||
                'notSelected',
            }));
            setPepList([...sortedResultWithAI]);
          } else {
            setPepList([...data]);
          }
        } else {
          const mergedPepResult = mergeAllDetails(data);
          const sortedResult = sortByParty(mergedPepResult);
          if (generateAI) {
            const sortedResultWithAI = sortedResult.map((item, index) => ({
              ...item,
              comments:
                Dummy_PEP_DATA[queryParams.username]?.comments[index] ||
                DEFAULT_COMMENT,
              status:
                Dummy_PEP_DATA[queryParams.username]?.status[index] ||
                'notSelected',
            }));
            setPepList(sortedResultWithAI);
          } else {
            setPepList(sortedResult);
          }
        }
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
      });
  };
  /**
   * @function fetchAmsList
   * @description a function to fetch AMS list
   * @param {object} queryParams
   * @returns {Promise} returns an axios promise
   */
  const fetchAmsList = (queryParams) => {
    const isNameScreen = queryParams?.card_type === 'name_screen';
    const url = isNameScreen
      ? `${API_ENDPOINTS.ONGOING_SCREENING_CARD_DETAILS}${queryParams.id}/ams`
      : `${API_ENDPOINTS.ONGOING_SCREENING_TRANSACTION_ALL_ALERT_LIST}${queryParams.id}/ams`;
    const generateAI = queryParams?.generateAI;
    setIsLoading(true);
    axios
      .get(url)
      .then((response) => {
        const { data } = response;
        if (isNameScreen) {
          if (generateAI) {
            const sortedResultWithAI = data.map((item, index) => ({
              ...item,
              comments:
                Dummy_AMS_DATA[queryParams.username]?.comments[index] ||
                DEFAULT_COMMENT,
              status:
                Dummy_AMS_DATA[queryParams.username]?.status[index] ||
                'notSelected',
            }));
            setAmsList(sortedResultWithAI);
          } else {
            setAmsList(data);
          }
        } else {
          const mergedAmsResult = mergeAllDetails(data);
          const sortedResult = sortByParty(mergedAmsResult);
          if (generateAI) {
            const sortedResultWithAI = sortedResult.map((item, index) => ({
              ...item,
              comments:
                Dummy_AMS_DATA[queryParams.username]?.comments[index] ||
                DEFAULT_COMMENT,
              status:
                Dummy_AMS_DATA[queryParams.username]?.status[index] ||
                'notSelected',
            }));
            setAmsList(sortedResultWithAI);
          } else {
            setAmsList(sortedResult);
          }
        }
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
      });
  };
  /**
   * @function fetchCustomerHistoryList
   * @description a function to fetch customer history list
   * @param {object} params
   * @returns {Promise} returns an axios promise
   */
  const fetchCustomerHistoryList = (params) => {
    setIsLoading(true);
    return axios
      .get(API_ENDPOINTS.ONGOING_SCREENING_SUMMARY_CUSTOMER_INFO, {
        params: {
          screen: 'ongoing',
          id: params.id,
          page: params.pageIndex + 1,
          pagesize: params.pageSize,
        },
      })
      .then(
        (response) => {
          setIsLoading(false);
          setCustomerHistoryList(response?.data);
          return response;
        },
        () => {
          setIsLoading(false);
        }
      );
  };

  /**
   * @function fetchReviewedSearchDetails
   * @description a function to fetch reviewed search details with verdicts
   * @param {object} params - contains search_id and screen_type
   * @returns {Promise} returns an axios promise
   */
  const fetchReviewedSearchDetails = (params) => {
    const body = {
      search_id: params.search_id,
      rank: params?.rank ?? 0,
      screen_type: params.screen_type,
    };

    return axios.post(API_ENDPOINTS.DASHBOARD_USER_HISTORY, body).then(
      (response) => {
        return response;
      },
      (error) => {
        throw error;
      }
    );
  };

  /**
   * @function fetchLegalList
   * @description A function to fetch legal List
   * @returns {Promise} returns an axios promise
   */
  const fetchLegalList = (queryParams) => {
    const isNameScreen = queryParams?.card_type === 'name_screen';
    const url = isNameScreen
      ? `${API_ENDPOINTS.ONGOING_SCREENING_CARD_DETAILS}${queryParams.id}/legal`
      : `${API_ENDPOINTS.ONGOING_SCREENING_TRANSACTION_ALL_ALERT_LIST}${queryParams.id}/legal`;
    setIsLoading(true);
    axios
      .get(url)
      .then((response) => {
        const { data } = response;
        if (isNameScreen) {
          setLegalList([...data]);
        } else {
          const mergedLegalResult = mergeAllDetails(data);
          setLegalList(sortByParty(mergedLegalResult));
        }
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
      });
  };
  /**
   * @function fetchCorporateList
   * @description A function to fetch corporate List
   * @returns {Promise} returns an axios promise
   */
  const fetchCorporateList = (queryParams) => {
    const isNameScreen = queryParams?.card_type === 'name_screen';
    const url = isNameScreen
      ? `${API_ENDPOINTS.ONGOING_SCREENING_CARD_DETAILS}${queryParams.id}/corporate`
      : `${API_ENDPOINTS.ONGOING_SCREENING_TRANSACTION_ALL_ALERT_LIST}${queryParams.id}/corporate`;
    setIsLoading(true);
    axios
      .get(url)
      .then((response) => {
        const { data } = response;
        if (isNameScreen) {
          setCorporateList([...data]);
        } else {
          const mergedCorporateResult = mergeAllDetails(data);
          setCorporateList(sortByParty(mergedCorporateResult));
        }
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
      });
  };
  /**
   * @function fetchLegalDocument
   * @description A function to fetch legal document
   * @returns {Promise} returns an axios promise
   */
  const fetchLegalDocument = (queryParams) => {
    setIsLoading(true);
    axios
      .get(
        `${API_ENDPOINTS.ONGOING_SCREENING_SUMMARY_LEGAL_DOCUMENT}${queryParams.tid}`
      )
      .then((response) => {
        setIsLoading(false);
        setLegalDocument(response?.data?.legal_documents);
      })
      .catch(() => {
        setIsLoading(false);
      });
  };

  /**
   * @function updateListsRow
   * @description a function to update list rows
   * @param {object} listType
   * @param {number} index
   * @param {string} value
   * @return {object} prevState
   */
  const updateListsRow = ({ index, value }, listType) => {
    switch (listType) {
      case 'sanctions':
        setSanctionsList((prevState) => {
          const updatedList = [...prevState];
          updatedList[index].status = value;
          return [...updatedList];
        });
        break;
      case 'sanctionsCountry':
        setSanctionsList((prevState) => {
          const updatedList = [...prevState];
          updatedList[index].status = value;
          return [...updatedList];
        });
        break;
      case 'sanctionsMnrl':
        setSanctionsList((prevState) => {
          const updatedList = [...prevState];
          updatedList[index].status = value;
          return [...updatedList];
        });
        break;
      case 'pep':
        setPepList((prevState) => {
          const updatedList = [...prevState];
          updatedList[index].status = value;
          return [...updatedList];
        });
        break;
      case 'ams':
        setAmsList((prevState) => {
          const updatedList = [...prevState];
          updatedList[index].status = value;
          return [...updatedList];
        });
        break;
      case 'legal':
        setLegalList((prevState) => {
          const updatedList = [...prevState];
          updatedList[index].status = value;
          return [...updatedList];
        });
        break;
      case 'corporate':
        setCorporateList((prevState) => {
          const updatedList = [...prevState];
          updatedList[index].status = value;
          return [...updatedList];
        });
        break;
      case 'risk':
        setRiskList((prevState) => {
          const updatedList = [...prevState];
          updatedList[index].classification = value;
          return [...updatedList];
        });
        break;
      default:
    }
  };
  const updateAllListsRow = (listType, mode) => {
    switch (listType) {
      case 'sanctions':
        setSanctionsList((prevState) => {
          const updatedList = prevState.map((item) => ({
            ...item,
            status: mode ? 'selected' : mode === null ? false : 'notSelected',
          }));
          return updatedList;
        });
        break;
      case 'sanctionsCountry':
        setSanctionsList((prevState) => {
          const updatedList = prevState.map((item) => ({
            ...item,
            status: mode ? 'selected' : mode === null ? false : 'notSelected',
          }));
          return updatedList;
        });
        break;
      case 'sanctionsMnrl':
        setSanctionsList((prevState) => {
          const updatedList = prevState.map((item) => ({
            ...item,
            status: mode ? 'selected' : mode === null ? false : 'notSelected',
          }));
          return updatedList;
        });
        break;
      case 'pep':
        setPepList((prevState) => {
          const updatedList = prevState.map((item) => ({
            ...item,
            status: mode ? 'selected' : mode === null ? false : 'notSelected',
          }));
          return updatedList;
        });
        break;
      case 'ams':
        setAmsList((prevState) => {
          const updatedList = prevState.map((item) => ({
            ...item,
            status: mode ? 'selected' : mode === null ? false : 'notSelected',
          }));
          return updatedList;
        });
        break;
      case 'legal':
        setLegalList((prevState) => {
          const updatedList = prevState.map((item) => ({
            ...item,
            status: mode ? 'selected' : mode === null ? false : 'notSelected',
          }));
          return updatedList;
        });
        break;
      case 'corporate':
        setCorporateList((prevState) => {
          const updatedList = prevState.map((item) => ({
            ...item,
            status: mode ? 'selected' : mode === null ? false : 'notSelected',
          }));
          return updatedList;
        });
        break;
      case 'risk':
        setRiskList((prevState) => {
          const updatedList = [...prevState];
          updatedList[index].classification = value;
          return [...updatedList];
        });
        break;
      default:
    }
  };
  /**
   * @function updateListsCommentRow
   * @description A function for updating comments from Lists Row
   * @returns {Promise} returns an axios promise
   */
  const updateListsCommentRow = ({ index, value }, listType) => {
    switch (listType) {
      case 'sanctions':
        setSanctionsList((prevState) => {
          const updatedList = [...prevState];
          updatedList[index].comments = value;
          return [...updatedList];
        });
        break;
      case 'sanctionsCountry':
        setSanctionsList((prevState) => {
          const updatedList = [...prevState];
          updatedList[index].comments = value;
          return [...updatedList];
        });
        break;
      case 'sanctionsMnrl':
        setSanctionsList((prevState) => {
          const updatedList = [...prevState];
          updatedList[index].comments = value;
          return [...updatedList];
        });
        break;
      case 'pep':
        setPepList((prevState) => {
          const updatedList = [...prevState];
          updatedList[index].comments = value;
          return [...updatedList];
        });
        break;
      case 'ams':
        setAmsList((prevState) => {
          const updatedList = [...prevState];
          updatedList[index].comments = value;
          return [...updatedList];
        });
        break;
      case 'legal':
        setLegalList((prevState) => {
          const updatedList = [...prevState];
          updatedList[index].comments = value;
          return [...updatedList];
        });
        break;
      case 'corporate':
        setCorporateList((prevState) => {
          const updatedList = [...prevState];
          updatedList[index].comments = value;
          return [...updatedList];
        });
        break;
      default:
    }
  };

  function getObjectIndexById(array, id) {
    return array?.findIndex((obj) => obj?.id === id);
  }
  const updateAttachmentInCommentRow = (
    fileDetails,
    URL,
    listType,
    operationType = 'add'
  ) => {
    switch (listType) {
      case 'sanctions':
        setSanctionsList((prevState) => {
          const updatedList = [...prevState];
          const index = getObjectIndexById(
            sanctionsList,
            fileDetails?.split('*')?.[0]
          );
          if (operationType !== 'delete') {
            updatedList[index].attached_file = fileDetails;
            updatedList[index].attached_file_url = URL;
          } else {
            updatedList[index].attached_file = '';
            updatedList[index].attached_file_url = URL;
          }

          return [...updatedList];
        });
        break;
      case 'sanctionsCountry':
        setSanctionsList((prevState) => {
          const updatedList = [...prevState];
          const index = getObjectIndexById(
            sanctionsList,
            fileDetails?.split('*')?.[0]
          );
          if (operationType !== 'delete') {
            updatedList[index].attached_file = fileDetails;

            updatedList[index].attached_file_url = URL;
          } else {
            updatedList[index].attached_file = '';
            updatedList[index].attached_file_url = URL;
          }

          return [...updatedList];
        });
        break;
      case 'sanctionsMnrl':
        setSanctionsList((prevState) => {
          const updatedList = [...prevState];
          const index = getObjectIndexById(
            sanctionsList,
            fileDetails?.split('*')?.[0]
          );
          if (operationType !== 'delete') {
            updatedList[index].attached_file = fileDetails;

            updatedList[index].attached_file_url = URL;
          } else {
            updatedList[index].attached_file = '';
            updatedList[index].attached_file_url = URL;
          }

          return [...updatedList];
        });
        break;
      case 'pepList':
        setPepList((prevState) => {
          const updatedList = [...prevState];
          const index = getObjectIndexById(
            pepList,
            fileDetails?.split('*')?.[0]
          );
          if (operationType !== 'delete') {
            updatedList[index].attached_file = fileDetails;
            updatedList[index].attached_file_url = URL;
          } else {
            updatedList[index].attached_file = '';
            updatedList[index].attached_file_url = URL;
          }

          return [...updatedList];
        });
        break;
      case 'amsList':
        setAmsList((prevState) => {
          const updatedList = [...prevState];
          const index = getObjectIndexById(
            amsList,
            fileDetails?.split('*')?.[0]
          );
          if (operationType !== 'delete') {
            updatedList[index].attached_file = fileDetails;
            updatedList[index].attached_file_url = URL;
          } else {
            updatedList[index].attached_file = '';
            updatedList[index].attached_file_url = URL;
          }
          return [...prevState];
        });
        break;
      case 'legal':
        setLegalList((prevState) => {
          const updatedList = [...prevState];
          const index = getObjectIndexById(
            prevState,
            fileDetails?.split('*')?.[0]
          );
          if (operationType !== 'delete') {
            updatedList[index].attached_file = fileDetails;
            updatedList[index].attached_file_url = URL;
          } else {
            updatedList[index].attached_file = '';
            updatedList[index].attached_file_url = URL;
          }
          return [...updatedList];
        });
        break;
      case 'corporate':
        setCorporateList((prevState) => {
          const updatedList = [...prevState];
          const index = getObjectIndexById(
            prevState,
            fileDetails?.split('*')?.[0]
          );
          if (operationType !== 'delete') {
            updatedList[index].attached_file = fileDetails;
            updatedList[index].attached_file_url = URL;
          } else {
            updatedList[index].attached_file = '';
            updatedList[index].attached_file_url = URL;
          }
          return [...updatedList];
        });
        break;
      default:
    }
  };
  /**
   * @function submitSummaryVerdict
   * @description a function to submit a summary
   * @param {object} data
   * @returns {Promise} returns an axios promise
   */
  const submitSummaryVerdict = (data) => {
    setIsLoading(true);
    const payload = {
      remarks: data.remarks,
      status: (data.verdict?.toLowerCase())[0] === 'a' ? 'a' : 'r',
      results: {
        ams_result: data.ams_result,
        pep_result: data.pep_result,
        sanction_result: data.sanction_result,
        sanction_country_result: data.sanction_country_result,
        mnrl_result: data.mnrl_result,
        legal_result: data.legal_result,
        corporate_result: data.corporate_result,
        ...(data?.risk
          ? {
              risk_rating_result: data?.risk_rating_result,
              risk_type: data?.risk_type,
              risk_rating: data?.risk_rating,
            }
          : {}),
      },
      attachment: data?.attachment,
    };
    return axios
      .post(
        `${API_ENDPOINTS.ONGOING_SCREENING_SUMMARY_VERDICT}${data.id}/`,
        payload,
        {
          params: { analyst: data.analyst, level: data.level },
        }
      )
      .then(
        () => {
          setIsLoading(false);
        },
        () => {
          setIsLoading(false);
        }
      );
  };

  const submitBulkVerdict = (payload) => {
    setIsLoading(true);
    return postBulkVerdict({
      scope: 'ongoing',
      payload,
      enqueueSnackbar,
    }).finally(() => setIsLoading(false));
  };
  /**
   * @function submitRequestReferral
   * @description A function to submit Request Referral
   * @returns {Promise} returns an axios promise
   */
  const submitRequestReferral = (data) => {
    setIsLoading(true);
    const payload = {
      description: data.remarks,
      requested_to: data.analystName,
      attachment: data?.attachment,
      ...(data?.transaction_my_queue_referral_submit && {
        transaction_my_queue_referral_submit:
          data?.transaction_my_queue_referral_submit,
      }),
      results: {
        ...(data?.transaction_my_queue_referral_submit
          ? data?.results
          : {
              sanction_result: data.sanction_result,
              pep_result: data.pep_result,
              ams_result: data.ams_result,
              corporate_result: data.corporate_result,
              legal_result: data.legal_result,
              sanction_country_result: data?.sanction_country_result,
              risk_rating_result: data?.risk_rating_result,
            }),
      },
    };
    return axios
      .post(
        `${API_ENDPOINTS.ONGOING_SCREENING_REQUEST_ANALYST}${data.id}/`,
        payload
      )
      .then(
        () => {
          setIsLoading(false);
          enqueueSnackbar('Refer sent successfully.', {
            variant: 'success',
          });
        },
        () => {
          setIsLoading(false);
        }
      );
  };
  /**
   * @function submitReferral
   * @description A function submit Referral
   * @returns {Promise} returns an axios promise
   */
  const submitReferral = (data) => {
    setIsLoading(true);
    const payload = {
      comments: data.remarks,
      attachment: data?.attachment,
    };
    return axios
      .post(
        `${API_ENDPOINTS.INITIAL_SCREENING_REQUEST_ANALYST}${data.id}/`,
        payload
      )
      .then(
        () => {
          setIsLoading(false);
          enqueueSnackbar('Refer sent successfully.', {
            variant: 'success',
          });
        },
        () => {
          setIsLoading(false);
        }
      );
  };
  const deleteFile = ({ id, row, type }) => {
    const data = { filename: `${id}*${row?.attached_file}` };
    return axios
      .delete(`${API_ENDPOINTS.DELETE_FILE}`, { params: data })
      .then((res) => {
        updateAttachmentInCommentRow(
          `${id}*${row?.attached_file}`,
          '',
          type,
          'delete'
        );
        return res;
      })
      .catch((error) => {
        enqueueSnackbar(error?.response?.data?.error, {
          variant: 'error',
        });
        throw error;
      });
  };
  /**
   * @function fetchTemplateList
   * @description A function to fetch team data
   * @returns {Promise} returns an axios promise
   */
  const fetchTemplateList = () => {
    setIsLoading(true);
    return axios.get(API_ENDPOINTS.SAR_CONFIGURATION).then(
      (response) => {
        setIsLoading(false);
        setTemplateList(response?.data);
      },
      (e) => {
        setIsLoading(false);
      }
    );
  };

  /**
   * @function fetchTemplateList
   * @description A function to fetch team data
   * @returns {Promise} returns an axios promise
   */
  const fetchTemplate = (data) => {
    setIsLoading(true);
    return axios.get(API_ENDPOINTS.GET_TEMPLATE, { params: data }).then(
      (response) => {
        setIsLoading(false);
        setTemplate(response?.data);
        return response;
      },
      (e) => {
        setIsLoading(false);
      }
    );
  };
  const getOngoingTransferData = ({
    page = 0,
    pageSize = 5,
    filters = {},
  } = {}) => {
    const modifiedFilters = CoreUtils.buildModifiedFilters(filters || {});
    // Always pass all required keys, use empty string if not present
    const params = {
      page: page + 1 || '',
      pagesize: pageSize || '',
      progress_status: modifiedFilters?.searchStatus ?? '',
      start_date: modifiedFilters?.transferDateRange?.start
        ? moment(modifiedFilters?.transferDateRange?.start).format(
            consts.GLOBAL_DATE_FORMAT
          )
        : '',
      end_date: modifiedFilters?.transferDateRange?.end
        ? moment(modifiedFilters?.transferDateRange?.end).format(
            consts.GLOBAL_DATE_FORMAT
          )
        : '',
      searched_field: modifiedFilters?.search ?? '',
      to_team: modifiedFilters?.transferredTeam ?? '',
      to_user: modifiedFilters?.transferredUser ?? '',
      transfer_type: modifiedFilters?.transferType ?? '',
      transaction_screening:
        modifiedFilters?.module === 'transaction_screening',
      search_format: modifiedFilters?.search_format ?? '',
    };
    return axios
      .get(`${API_ENDPOINTS.INITIAL_ONGOING_TRANSFER_CASE_LIST}ongoing`, {
        params,
        loader: false,
      })
      .then((res) => {
        return {
          data: res?.data,
          totalRowCount: res.headers?.['x-total-count'],
        };
      });
  };

  /**
   * @function handleTransactionReWork
   * @description A function to post transaction re work details
   * @returns {Promise} returns an axios promise
   */
  const handleTransactionReWork = (queryParams) => {
    setIsLoading(true);
    resetAllList();

    const payload = {
      comment: queryParams?.comment,
      attachment: queryParams?.file?.name,
      results: queryParams?.results,
    };

    return axios
      .post(
        `${API_ENDPOINTS.ONGOING_TRANSACTION_REWORK}${queryParams?.data?.internal_transaction_id}/`,
        payload
      )
      .then(
        (response) => {
          setIsLoading(false);
          if (response?.data) {
            enqueueSnackbar(response?.data?.message, {
              variant: 'success',
            });
            setTimeout(() => {
              window.location.reload();
            }, 1000);
          }
        },
        (response) => {
          setIsLoading(false);
          enqueueSnackbar(response?.response?.data?.message, {
            variant: 'error',
          });
        }
      );
  };

  const submitTransactionSummaryVerdict = (data) => {
    setIsLoading(true);
    const payload = {
      remarks: data.remarks,
      status: data.verdict?.toLowerCase()?.[0] === 'a' ? 'a' : 'r',
      results: data?.results,
      attachment: data?.attachment,
      move_search_to_om: data?.move_search_to_om,
    };
    return axios
      .post(
        `${API_ENDPOINTS.ONGOING_SCREENING_TRANSACTION_VERDICT}${data.id}/`,
        payload,
        {
          params: { analyst: data.analyst, level: data.level },
        }
      )
      .then(
        () => {
          setIsLoading(false);
          enqueueSnackbar('Verdict submitted successfully.', {
            variant: 'success',
          });
        },
        () => {
          setIsLoading(false);
        }
      );
  };

  return {
    user,
    fetchUsers,
    userList,
    setUserList,
    updateUser,
    fetchSanctionsList,
    fetchPepList,
    fetchAmsList,
    fetchLegalList,
    fetchCorporateList,
    fetchCustomerHistoryList,
    fetchReviewedSearchDetails,
    sanctionsList,
    pepList,
    amsList,
    legalList,
    corporateList,
    legalDocument,
    fetchLegalDocument,
    CustomerHistoryList,
    updateListsRow,
    isLoading,
    submitSummaryVerdict,
    submitBulkVerdict,
    fetchUsersPageCount,
    totalUsers,
    disabled,
    setdisabled,
    totalCustomer,
    setTotalCustomer,
    fetchCardData,
    cardDetails,
    setCardDetails,
    analystsList,
    fetchReferralsList,
    submitReferral,
    submitRequestReferral,
    updateListsCommentRow,
    referralList,
    hasData,
    setHasData,
    riskList,
    riskValue,
    riskResult,
    fetchRiskList,
    calculateRiskRating,
    getRiskParameters,
    industry,
    product,
    occupation,
    countryList,
    fetchCountryList,
    setRiskValue,
    setRiskList,
    systemRisk,
    setSystemRisk,
    riskResponse,
    riskDetails,
    getRiskRating,
    updateRiskRating,
    crrMandatoryParameterList,
    getTransactionParameters,
    channel,
    productType,
    transactionType,
    updateAttachmentInCommentRow,
    uploadAttachedFiles,
    deleteFile,
    uploadFileOns3,
    handleReview,
    updateAllListsRow,
    fetchTemplateList,
    templateList,
    fetchTemplate,
    template,
    riskParameters,
    fetchUserList,
    fetchTransactionCardData,
    fetchTransactionUsers,
    getOngoingTransferData,
    getOngoingScreeningData,
    getOngoingTransactionUsers,
    ongoingScreeningTotalCount,
    submitTransactionSummaryVerdict,
  };
}
